// src/admin/components/header/Header.jsx - Apartment Management System
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  RiMenu4Line,
  RiMoonLine,
  RiSunLine,
  RiNotificationLine,
  RiUserLine,
  RiSettings4Line,
  RiLogoutBoxLine,
  RiGlobalLine,
  RiArrowDownSLine,
  RiMessage3Line,
  RiTimeLine } from
'react-icons/ri';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';
import { formatRoleName, canOperateSystem } from '../../../utils/roles';

const Header = ({ sidebarOpen, setSidebarOpen }) => {
  const { t, i18n } = useTranslation();
  const { isDarkMode, toggleDarkMode, isRTL, toggleRTL } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);

  // Live clock — updates every second
  const [clock, setClock] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatClock = (date) => {
    const locale = i18n.language || 'en';
    const day = date.toLocaleDateString(locale, { weekday: 'short' });
    const dateStr = date.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
    const timeStr = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    return { day, dateStr, timeStr };
  };
  const { day, dateStr, timeStr } = formatClock(clock);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await api.get('/logs', { params: { limit: 20 } });
        if (response.data.success && response.data.logs) {
          // Filter out read events to keep notifications for actual changes
          const meaningfulLogs = response.data.logs.filter((log) => !log.action.startsWith('view_')).slice(0, 5);

          // Format logs to notifications
          const formattedNotifs = meaningfulLogs.map((log) => {
            let msg = '';
            // For logins, just show username
            if (log.action === 'user_login') {
              msg = `User ${log.entityName || 'logged in'}`;
              // For other actions, try to show entity name, otherwise show type and action
            } else {
              msg = log.entityName ? `${log.entityType}: ${log.entityName}` : `${log.entityType} updated`;
            }

            return {
              id: log.id,
              title: log.action.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
              message: msg,
              time: new Date(log.createdAt).toLocaleDateString(),
              unread: false
            };
          });
          setNotifications(formattedNotifs);
          setUnreadCount(0);
        }
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      }
    };

    if (user && canOperateSystem(user.role)) {
      fetchNotifications();
    }
  }, [user]);

  // Message Polling
  useEffect(() => {
    if (!user || !canOperateSystem(user.role)) return;

    const fetchUnread = async () => {
      try {
        const res = await api.get('/messages/unread-count');
        if (res.data?.success) setUnreadMessages(res.data.count || 0);
      } catch (e) {

        // silently ignore error
      }};

    fetchUnread();
    const intervalId = setInterval(fetchUnread, 30000);
    return () => clearInterval(intervalId);
  }, [user]);

  const getTrialDaysLeft = () => {
    if (user?.subscriptionStatus === 'trial' && user?.trialEndsAt) {
      const endsAt = new Date(user.trialEndsAt);
      const now = new Date();
      const diffTime = endsAt - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays); // Ensure it doesn't go below 0
    }
    return null;
  };

  const trialDaysLeft = getTrialDaysLeft();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setLanguageDropdownOpen(false);

    const rtlLangs = ['ar', 'ku'];
    if (rtlLangs.includes(lng) && !isRTL) {
      toggleRTL();
    } else if (!rtlLangs.includes(lng) && isRTL) {
      toggleRTL();
    }
  };

  const getCurrentLanguageDisplay = () => {
    const names = {
      en: 'English',
      ar: 'العربية',
      ku: 'کوردی',
      de: 'Deutsch',
      fr: 'Français',
      it: 'Italiano',
      sp: 'Español'
    };
    return names[i18n.language] || 'English';
  };

  const closeAllDropdowns = () => {
    setProfileDropdownOpen(false);
    setLanguageDropdownOpen(false);
    setNotificationDropdownOpen(false);
  };

  return (
    <>
      <header className={`relative h-16 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} border-b flex items-center justify-between px-4 md:px-6 w-full`
      }>

        {/* Left Section */}
        <div className="flex items-center space-x-3 md:space-x-4">
          {/* Mobile Sidebar Toggle */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`p-2 rounded-lg md:hidden ${isDarkMode ? 'hover:bg-zinc-900 text-gray-300' : 'hover:bg-gray-100 text-gray-600'} transition-colors`
            }
            aria-label="Toggle sidebar">
            
            <RiMenu4Line size={20} />
          </button>

          {/* Desktop Sidebar Toggle */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`hidden md:block p-2 rounded-lg ${isDarkMode ? 'hover:bg-zinc-900 text-gray-300' : 'hover:bg-gray-100 text-gray-600'} transition-colors`
            }
            aria-label="Toggle sidebar">
            
            <RiMenu4Line size={20} />
          </button>

          {/* Live Date & Time */}
          <div className={`hidden sm:flex items-center gap-2.5 px-3 py-1.5 rounded-xl border ${
          isDarkMode ?
          'bg-zinc-800/60 border-zinc-700 text-gray-200' :
          'bg-gray-50 border-gray-200 text-gray-700'}`
          }>
            <RiTimeLine size={16} className={isDarkMode ? 'text-red-400' : 'text-red-500'} />
            <div className="flex items-baseline gap-1.5 leading-none">
              <span className={`text-xs font-semibold uppercase tracking-wide ${
              isDarkMode ? 'text-red-400' : 'text-red-500'}`
              }>{day}</span>
              <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{dateStr}</span>
              <span className={`text-sm font-mono font-bold tabular-nums ${
              isDarkMode ? 'text-white' : 'text-gray-900'}`
              }>{timeStr}</span>
            </div>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center space-x-2 md:space-x-4">

          {/* Trial Badge */}
          {trialDaysLeft !== null &&
          <button
            onClick={() => navigate('/admin/settings/subscription')}
            className={`hidden sm:flex items-center px-3 py-1 rounded-full text-xs font-medium border cursor-pointer hover:opacity-80 transition-opacity ${trialDaysLeft <= 3 ?
            'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800' :
            'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-800'}`
            }>
              {t("trial_ends_in")} {trialDaysLeft} {t("days_subscribe")}
            </button>
          }

          {/* Language Selector */}
          <div className="relative">
            <button
              onClick={() => {
                setLanguageDropdownOpen(!languageDropdownOpen);
                setProfileDropdownOpen(false);
                setNotificationDropdownOpen(false);
              }}
              className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-zinc-800 text-gray-300' : 'hover:bg-gray-100 text-gray-600'} flex items-center space-x-1 transition-colors`
              }>
              
              <RiGlobalLine size={20} />
              <span className="text-sm font-medium hidden sm:block">
                {getCurrentLanguageDisplay()}
              </span>
              <RiArrowDownSLine size={16} />
            </button>

            {languageDropdownOpen &&
            <div className={`absolute end-0 mt-2 w-48 rounded-lg shadow-lg ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} border z-50`
            }>
                <div className="py-1">
                  {[
                { code: 'en', label: 'English' },
                { code: 'ar', label: 'العربية' },
                { code: 'ku', label: 'کوردی' },
                { code: 'de', label: 'Deutsch' },
                { code: 'fr', label: 'Français' },
                { code: 'it', label: 'Italiano' },
                { code: 'sp', label: 'Español' }].
                map(({ code, label }) =>
                <button
                  key={code}
                  onClick={() => changeLanguage(code)}
                  className={`flex items-center w-full px-4 py-2 text-sm transition-colors ${
                  i18n.language === code ?
                  isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-zinc-50 text-red-600' :
                  isDarkMode ? 'text-gray-300 hover:bg-zinc-800' : 'text-gray-700 hover:bg-gray-100'}`
                  }>
                  
                      
                      {label}
                    </button>
                )}
                </div>
              </div>
            }
          </div>

          {/* Theme Toggle */}
          <button
            onClick={toggleDarkMode}
            className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-zinc-900 text-gray-300' : 'hover:bg-gray-100 text-gray-600'} transition-colors`
            }
            aria-label="Toggle theme">
            
            {isDarkMode ? <RiSunLine size={20} /> : <RiMoonLine size={20} />}
          </button>

          {/* Messages */}
          {canOperateSystem(user?.role) &&
          <button
            onClick={() => navigate('/admin/messages')}
            className={`relative p-2 rounded-lg ${isDarkMode ? 'hover:bg-zinc-800 text-gray-300' : 'hover:bg-gray-100 text-gray-600'} transition-colors`
            }
            aria-label="Messages">
            
              <RiMessage3Line size={20} />
              {unreadMessages > 0 &&
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center shadow-sm">
                  {unreadMessages > 9 ? '9+' : unreadMessages}
                </span>
            }
            </button>
          }

          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => {
                setNotificationDropdownOpen(!notificationDropdownOpen);
                setProfileDropdownOpen(false);
                setLanguageDropdownOpen(false);
              }}
              className={`relative p-2 rounded-lg ${isDarkMode ? 'hover:bg-zinc-800 text-gray-300' : 'hover:bg-gray-100 text-gray-600'} transition-colors`
              }
              aria-label="Notifications">
              
              <RiNotificationLine size={20} />
              {unreadCount > 0 &&
              <span className="absolute -top-1 -right-1 bg-zinc-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              }
            </button>

            {notificationDropdownOpen &&
            <div className={`absolute end-0 mt-2 w-80 rounded-lg shadow-lg ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} border z-50`
            }>
                <div className={`p-4 border-b ${isDarkMode ? 'border-zinc-800' : 'border-gray-200'}`}>
                  <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {t('notifications')}
                  </h3>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {notifications.map((notification) =>
                <div
                  key={notification.id}
                  className={`p-4 border-b ${isDarkMode ? 'border-zinc-800' : 'border-gray-200'} hover:bg-opacity-50 transition-colors ${isDarkMode ? 'hover:bg-zinc-800' : 'hover:bg-gray-50'} ${
                  notification.unread ? isDarkMode ? 'bg-red-900/10' : 'bg-zinc-50' : ''}`
                  }>
                  
                      <div className="flex items-start space-x-3">
                        <div className={`w-2 h-2 rounded-full mt-2 ${notification.unread ? 'bg-zinc-500' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`
                    } />
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`
                      }>
                            {notification.title}
                          </p>
                          <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`
                      }>
                            {notification.message}
                          </p>
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`
                      }>
                            {notification.time}
                          </p>
                        </div>
                      </div>
                    </div>
                )}
                </div>
              </div>
            }
          </div>

          {/* User Profile Dropdown */}
          <div className="relative">
            <button
              onClick={() => {
                setProfileDropdownOpen(!profileDropdownOpen);
                setLanguageDropdownOpen(false);
                setNotificationDropdownOpen(false);
              }}
              className={`flex items-center space-x-2 p-1 rounded-lg ${isDarkMode ? 'hover:bg-zinc-800' : 'hover:bg-gray-100'} transition-colors`
              }>
              
              <div className="hidden md:block text-left">
                <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`
                }>
                  {user?.firstName} {user?.lastName}
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`
                }>
                  {formatRoleName(user?.role)}
                  {user?.subscriptionTier &&
                  <span className={`ml-2 px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${user.subscriptionTier === 'enterprise' ?
                  'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                  user.subscriptionTier === 'pro' ?
                  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                  'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'}`
                  }>
                      {user.subscriptionTier}
                    </span>
                  }
                </p>
              </div>
              <RiArrowDownSLine className={`${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`
              } size={16} />
            </button>

            {profileDropdownOpen &&
            <div className={`absolute end-0 mt-2 w-48 rounded-lg shadow-lg ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} border z-50`
            }>
                <div className="py-1">
                  <button
                  onClick={() => {
                    navigate('/admin/settings/profile');
                    setProfileDropdownOpen(false);
                  }}
                  className={`flex items-center w-full px-4 py-2 text-sm transition-colors ${isDarkMode ? 'text-gray-300 hover:bg-zinc-800' : 'text-gray-700 hover:bg-gray-100'}`
                  }>
                  
                    <RiUserLine className="me-2" size={16} />
                    {t('profile')}
                  </button>
                  <button
                  onClick={() => {
                    navigate('/admin/settings');
                    setProfileDropdownOpen(false);
                  }}
                  className={`flex items-center w-full px-4 py-2 text-sm transition-colors ${isDarkMode ? 'text-gray-300 hover:bg-zinc-800' : 'text-gray-700 hover:bg-gray-100'}`
                  }>
                  
                    <RiSettings4Line className="me-2" size={16} />
                    {t('settings')}
                  </button>
                  <hr className={`my-1 ${isDarkMode ? 'border-zinc-800' : 'border-gray-200'}`} />
                  <button
                  onClick={handleLogout}
                  className={`flex items-center w-full px-4 py-2 text-sm transition-colors ${isDarkMode ? 'text-red-400 hover:bg-zinc-800' : 'text-red-600 hover:bg-gray-100'}`
                  }>
                  
                    <RiLogoutBoxLine className="me-2" size={16} />
                    {t('logout')}
                  </button>
                </div>
              </div>
            }
          </div>
        </div>
      </header>

      {/* Click outside handlers */}
      {(profileDropdownOpen || languageDropdownOpen || notificationDropdownOpen) &&
      <div
        className="fixed inset-0 z-40"
        onClick={closeAllDropdowns} />

      }
    </>);

};

export default Header;